import { Col, Row } from "antd";
import { get, isEmpty, set } from "lodash";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdWorkOutline } from "react-icons/md";
import Form from "shared/components/Form";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  JOB_LIST,
  MILTIS_PRODUCT_CODES,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Activity = ({
  formProps,
  values,
  setPercent,
  setFieldValue,
  disabled,
  setDisabled,
  isFieldRequired,
  initialValues,
  errors,
  subscription,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  // Check if this is AURA_TER product
  const isAuraTer = useMemo(
    () =>
      get(subscription, "insurancePlan.product") ===
      MILTIS_PRODUCT_CODES.AURA_TER,
    [subscription],
  );

  // Mapping profession to exerciseFrame code
  const PROFESSION_CODE_MAP = useMemo(
    () => ({
      [SOCIO_PROFESSIONAL_CATEGORIES.FARMERS]: "500", // Agriculteurs exploitants
      [SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS]: "410", // Artisans
      [SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR]: "430", // Commerçants et assimilés
      [SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS]: "420", // Chefs d'entreprise
      [SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR]: "450", // Professions libérales et assimilées
      [SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES]:
        "280", // Cadres et employés de la fonction publique
      [SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES]: "220", // Cadre d'entreprise
      [SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS]: "242", // Employés et agents de maîtrise
      [SOCIO_PROFESSIONAL_CATEGORIES.WORKERS]: "243", // Ouvriers
      [SOCIO_PROFESSIONAL_CATEGORIES.RETIREES]: "700", // Retraités
      [SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY]:
        "290", // Personnes sans activité professionnelle
    }),
    [],
  );

  // CA1 & CA2: Set default profession to "Cadres et employés de la fonction publique" and disable it
  useEffect(() => {
    if (isAuraTer) {
      const currentProfession = get(
        initialValues,
        "project.contact.profession",
      );
      const defaultProfession =
        SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;

      // Set default profession if not already set
      if (!currentProfession) {
        set(initialValues, "project.contact.profession", defaultProfession);
        // Set exerciseFrame code for this profession
        set(
          initialValues,
          "project.contact.exerciseFrame",
          PROFESSION_CODE_MAP[defaultProfession],
        );
      }

      // Disable profession field
      if (setDisabled) {
        setDisabled((prev) => ({
          ...prev,
          project: {
            ...prev?.project,
            contact: {
              ...prev?.project?.contact,
              profession: true,
            },
          },
        }));
      }
    }
  }, [isAuraTer, initialValues, setDisabled, PROFESSION_CODE_MAP]);

  // CA3: Professional situation options for AURA_TER
  const PROFESSIONAL_SITUATION_OPTIONS = useMemo(
    () => [
      {
        value: JOB_LIST.ACTIVE,
        label:
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
            ?.PROFESSIONAL_SITUATION?.OPTIONS?.ACTIVE || "En activité",
      },
      {
        value: JOB_LIST.RETIRED,
        label:
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
            ?.PROFESSIONAL_SITUATION?.OPTIONS?.RETIRED || "Retraité",
      },
    ],
    [SUBSCRIPTION],
  );

  const PROFESSION_OPTIONS = useMemo(
    () => [
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.FARMERS ||
          "Agriculteurs exploitants",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.ARTISANS || "Artisans",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.TRADERS_AND_SIMILAR ||
          "Commerçants et assimilés",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.ENTREPRENEURS ||
          "Chefs d'entreprise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.LIBERAL_PROFESSIONS_AND_SIMILAR ||
          "Professions libérales et assimilées",
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES ||
          "Cadres et employés de la fonction publique",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.EXECUTIVES ||
          "Cadre d'entreprise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.EMPLOYEES_AND_SUPERVISORS ||
          "Employés et agents de maîtrise",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.WORKERS,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.WORKERS || "Ouvriers",
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
        label: SUBSCRIPTION.FORM.PROFESSION_OPTIONS?.RETIREES || "Retraités",
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY,
        label:
          SUBSCRIPTION.FORM.PROFESSION_OPTIONS
            ?.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY ||
          "Personnes sans activité professionnelle",
      },
    ],
    [SUBSCRIPTION],
  );

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Select
              options={PROFESSION_OPTIONS}
              icon={<MdWorkOutline size={16} />}
              placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
              label={SUBSCRIPTION.FORM.PROFESSION.LABEL}
              defaultValue={
                get(initialValues, "project.contact.profession") ||
                (isAuraTer
                  ? SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
                  : undefined)
              }
              errors={errors["project.contact.profession"]}
              touched={!isEmpty(errors["project.contact.profession"])}
              onChange={(value) => {
                set(initialValues, "project.contact.profession", value);
                // Set exerciseFrame code based on profession
                const exerciseFrameCode = PROFESSION_CODE_MAP[value];
                if (exerciseFrameCode) {
                  set(
                    initialValues,
                    "project.contact.exerciseFrame",
                    exerciseFrameCode,
                  );
                } else {
                  set(initialValues, "project.contact.exerciseFrame", null);
                }
              }}
              onBlur={() => setPercent(initialValues)}
              optional={
                !get(disabled, "project.contact.profession") &&
                !isFieldRequired("project.contact.profession") &&
                SUBSCRIPTION.FORM.OPTIONAL
              }
              required={isFieldRequired("project.contact.profession")}
              disabled={
                isAuraTer || get(disabled, "project.contact.profession")
              }
            />
          </Col>
          {/* CA3: Add "Situation professionnelle" field for AURA_TER */}
          {isAuraTer && (
            <Col lg={24} xs={24}>
              <Select
                options={PROFESSIONAL_SITUATION_OPTIONS}
                icon={<MdWorkOutline size={16} />}
                placeholder={
                  SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
                    ?.PROFESSIONAL_SITUATION?.PLACEHOLDER || "Sélectionnez"
                }
                label={
                  SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY
                    ?.PROFESSIONAL_SITUATION?.LABEL ||
                  "Situation professionnelle"
                }
                defaultValue={get(
                  initialValues,
                  "project.contact.fields.professionalSituation",
                )}
                errors={errors["project.contact.fields.professionalSituation"]}
                touched={
                  !isEmpty(
                    errors["project.contact.fields.professionalSituation"],
                  )
                }
                onChange={(value) => {
                  set(
                    initialValues,
                    "project.contact.fields.professionalSituation",
                    value,
                  );
                }}
                onBlur={() => setPercent(initialValues)}
                required={isFieldRequired(
                  "project.contact.fields.professionalSituation",
                )}
                disabled={get(
                  disabled,
                  "project.contact.fields.professionalSituation",
                )}
              />
            </Col>
          )}
          {/* {!get(disabled, "project.contact.optMadelin") && (
            <>
              <Col lg={24} xs={24}>
                <Input
                  type="radio"
                  variant="group"
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  icon={<MdOutlineSouthAmerica size={16} />}
                  label={SUBSCRIPTION.FORM.MADELIN_LAW.LABEL}
                  values={[
                    {
                      value: true,
                      label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.YES,
                    },
                    {
                      value: false,
                      label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.NO,
                    },
                  ]}
                  defaultValue={get(
                    initialValues,
                    "project.contact.optMadelin"
                  )}
                  errors={errors["project.contact.optMadelin"]}
                  touched={!isEmpty(errors["project.contact.optMadelin"])}
                  onChange={(value) =>
                    set(initialValues, "project.contact.optMadelin", value)
                  }
                  required={isFieldRequired("project.contact.optMadelin")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={SUBSCRIPTION.FORM.MADELIN_LAW.INFO}
                />
              </Col>
            </>
          )} */}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
